import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { first, pickBy, toPairs } from "lodash-es";
import { Observable, forkJoin, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { ReminderTemplateBackend } from "src/app/backend/api/reminder-template.backend";
import {
  Reminder,
  ReminderCreate,
  ReminderRecipient
} from "src/app/backend/models/reminder";
import { UserNotify } from "src/app/support/notify-decorator";
import { PaginatedResult } from "src/app/support/paginated-result";
import { validatePhone } from "src/app/support/phone";
import { environment } from "src/environments/environment";

import ObjectID from "bson-objectid";
import { BootstrapService } from "../../modules/bootstrap/bootstrap.service";
import { Customer } from "src/app/backend/models/customer/customer";

@Injectable()
export class ReminderBackend {
  private readonly http = inject(HttpClient);
  private readonly customerBackend = inject(CustomerBackend);
  private readonly bootstrapService = inject(BootstrapService);

  @UserNotify({
    successMessage: marker("Promemoria creato"),
    errorMessage: marker("Impossibile creare il promemoria")
  })
  create(payload: ReminderCreate): Observable<Reminder> {
    return this.createSilently(payload);
  }

  createSilently(payload: ReminderCreate): Observable<Reminder> {
    if (payload.data.computedProperties) {
      toPairs(payload.data.computedProperties).forEach(([key, value]) => {
        (<any>payload.data)[key.replaceAll("$", ".")] = value;
      });

      delete payload.data.computedProperties;
    }

    if (payload.data) {
      payload.data = <any>JSON.stringify(payload.data);
    }

    return this.http.post<Reminder>(
      `${environment.snoots.api.notification}/reminders`,
      payload
    );
  }

  load(id: string): Observable<Reminder> {
    return this.http.get<Reminder>(
      `${environment.snoots.api.notification}/reminders/${id}`
    );
  }

  list({
    page,
    size,
    filterValues,
    scheduledAtFrom,
    scheduledAtTo
  }: {
    page: number;
    size: number;
    filterValues?: Record<string, string | boolean | undefined>;
    scheduledAtFrom?: string | undefined;
    scheduledAtTo?: string | undefined;
  }): Observable<PaginatedResult<Reminder>> {
    return this.extendReminderRecipientsWithCustomer(
      this.http.get<PaginatedResult<Reminder>>(
        `${environment.snoots.api.notification}/reminders`,
        {
          params: <any>pickBy({
            p: page,
            s: size,
            scheduledAtFrom: scheduledAtFrom,
            scheduledAtTo: scheduledAtTo,
            ...filterValues
          })
        }
      )
    );
  }

  listByPatientId(
    patientId: string,
    {
      page,
      size,
      filterValues
    }: {
      page: number;
      size: number;
      filterValues?: Record<string, string | boolean | undefined>;
    }
  ): Observable<PaginatedResult<Reminder>> {
    return this.extendReminderRecipientsWithCustomer(
      this.http.get<PaginatedResult<Reminder>>(
        `${environment.snoots.api.notification}/reminders`,
        {
          params: {
            p: page,
            s: size,
            resource: patientId,
            ...filterValues
          }
        }
      )
    );
  }

  listByResource(resource: string): Observable<Reminder[]> {
    return this.http
      .get<PaginatedResult<Reminder>>(
        `${environment.snoots.api.notification}/reminders`,
        {
          params: {
            resource
          }
        }
      )
      .pipe(map((response) => response.docs));
  }

  @UserNotify({
    successMessage: marker("Promemoria aggiornato"),
    errorMessage: marker("Impossibile aggiornare il promemoria")
  })
  update({ reminderId }: { reminderId: string }): Observable<void> {
    return this.load(reminderId).pipe(
      switchMap(() => {
        return of(void 0);
      })
    );
  }

  @UserNotify({
    successMessage: marker("Promemoria eliminato"),
    errorMessage: marker("Impossibile eliminare il promemoria")
  })
  delete(id: string): Observable<void> {
    return this.deleteSilently(id);
  }

  deleteSilently(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.notification}/reminders/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Promemoria inviato"),
    errorMessage: marker("Impossibile impostare il promemoria come inviato")
  })
  send(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.notification}/reminders/${id}/send`,
      {}
    );
  }

  private extendReminderRecipientsWithCustomer(
    obs: Observable<PaginatedResult<Reminder>>
  ): Observable<PaginatedResult<Reminder>> | any {
    return obs.pipe(
      switchMap((paginatedResult) => {
        if (
          !paginatedResult ||
          !paginatedResult.docs ||
          paginatedResult.docs.length === 0
        ) {
          return of(paginatedResult);
        }

        return forkJoin([
          forkJoin(
            paginatedResult.docs.map((reminder: Reminder) => {
              if (reminder.recipients && reminder.recipients?.length > 0) {
                return forkJoin(
                  reminder.recipients.map((recipient: ReminderRecipient) => {
                    if (!recipient.id) {
                      return of(recipient);
                    }

                    return this.customerBackend
                      .loadWithoutPatients(recipient.id)
                      .pipe(
                        map((customer: Customer) => ({
                          ...recipient,
                          customer: customer,
                          hasValidPhone: validatePhone(
                            {
                              phone: customer.phone
                            },
                            this.bootstrapService
                          )
                        }))
                      );
                  })
                ).pipe(
                  map((recipientsWithCustomer) => ({
                    ...reminder,
                    recipients: recipientsWithCustomer
                  }))
                );
              } else {
                return of(reminder);
              }
            })
          ),
          forkJoin(
            paginatedResult.docs.map((reminder: Reminder) => {
              return of(reminder);
            })
          )
        ])
          .pipe(
            map(([recipientsResult, templatesResult]) => {
              return recipientsResult.map(
                (reminderWithRecipientsData: Reminder) => {
                  const reminderWithTemplate = templatesResult.find(
                    (reminderWithTemplateData: Reminder) =>
                      reminderWithTemplateData.id ===
                      reminderWithRecipientsData.id
                  );

                  if (reminderWithTemplate) {
                    return {
                      ...reminderWithTemplate,
                      ...reminderWithRecipientsData
                    };
                  }

                  return reminderWithRecipientsData;
                }
              );
            })
          )
          .pipe(
            map((remindersArray) => {
              return {
                docs: remindersArray,
                totalDocs: paginatedResult.totalDocs
              };
            })
          );
      })
    );
  }
}
